/**
 * KalenderPage — Zentrale Terminübersicht für alle Betriebe
 * DESIGN: Koop-Red (#C41E3A), professionell
 * MOBILE: Tagesansicht als Standard, kompakte Slots, responsive Controls
 * FEATURES: Tag/Woche/Monat, Google Calendar, Uhrzeit-Linie, Heute hervorgehoben
 * NEU: "Aufgabe erstellen" Button bei jedem Termin-Klick
 */

import { useTermine } from '@/hooks/useSupabase';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { BETRIEBE, BETRIEB_LIST, type BetriebId } from '@/lib/betriebe';
import { type BusinessUnit } from '@/lib/supabase';
import { useGoogleCalendar, type GoogleCalendarEvent } from '@/hooks/useGoogleCalendar';
import CreateTaskFromEventModal from '@/components/CreateTaskFromEventModal';
import KalenderSidebar from '@/components/KalenderSidebar';
import { useIsMobile } from '@/hooks/useMobile';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Calendar, Plus, ChevronLeft, ChevronRight, Filter, ChevronDown,
  MapPin, Users, RefreshCw, X, Clock, CheckSquare, Trash2, Pencil, Link,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useMobileBackHandler } from '@/hooks/useMobileBackHandler';
import { localIsoDate } from '@/lib/date-utils';
import { dedupeGoogleEvents, buildTerminLinkPayload } from '@/lib/calendarDedup';
import { getISOWeek, relativeLabel, snapHalfHour, splitHour, eventDurationHours } from '@/lib/calendarTime';
import { kategorienFuerBetrieb } from '@/lib/calendarCategories';

// ─── CONSTANTS ──────────────────────────────────────────────────────────────
const KOOP_RED = '#C41E3A';

const GCAL_COLORS: Record<string, string> = {
  '1': '#7986CB', '2': '#33B679', '3': '#8E24AA', '4': '#E67C73',
  '5': '#F6BF26', '6': '#F4511E', '7': '#039BE5', '8': '#616161',
  '9': '#3F51B5', '10': '#0B8043', '11': '#D50000',
};

type CalendarView = 'day' | 'week' | 'month' | 'agenda';

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  source: 'supabase' | 'google';
  businessUnit?: BetriebId;
  color: string;
  bgColor: string;
  location?: string;
  htmlLink?: string;
  beschreibung?: string;
  kategorie?: string;
  status?: string;
  akteId?: string;
  akteNummer?: string;
  kundeId?: string;
  kundeName?: string;
  erinnerungMinuten?: number;
}

// ─── COLLISION DETECTION ───────────────────────────────────────────────────
interface PositionedEvent {
  event: CalendarEvent;
  column: number;
  totalColumns: number;
}

function computeEventPositions(events: CalendarEvent[], startHour: number, totalHours: number): PositionedEvent[] {
  if (events.length === 0) return [];

  const overlaps = (a: CalendarEvent, b: CalendarEvent): boolean => {
    const aStart = new Date(a.start).getTime();
    const aEnd = a.end ? new Date(a.end).getTime() : aStart + 60 * 60000;
    const bStart = new Date(b.start).getTime();
    const bEnd = b.end ? new Date(b.end).getTime() : bStart + 60 * 60000;
    return aStart < bEnd && aEnd > bStart;
  };

  const sorted = [...events].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const columns: CalendarEvent[][] = [];
  const eventColumn: Map<string, number> = new Map();

  for (const evt of sorted) {
    let placed = false;
    for (let col = 0; col < columns.length; col++) {
      const colEvents = columns[col];
      const hasOverlap = colEvents.some(e => overlaps(e, evt));
      if (!hasOverlap) {
        columns[col].push(evt);
        eventColumn.set(evt.id, col);
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push([evt]);
      eventColumn.set(evt.id, columns.length - 1);
    }
  }

  const result: PositionedEvent[] = sorted.map(evt => {
    const col = eventColumn.get(evt.id) ?? 0;
    const group = sorted.filter(other => other.id === evt.id || overlaps(evt, other));
    const maxCol = Math.max(...group.map(e => eventColumn.get(e.id) ?? 0));
    return { event: evt, column: col, totalColumns: maxCol + 1 };
  });

  return result;
}

// ─── HELPERS ────────────────────────────────────────────────────────────────
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}


function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getWeekDates(date: Date): Date[] {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    return dd;
  });
}

function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function toISOLocal(date: Date) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ─── NEW EVENT DIALOG ───────────────────────────────────────────────────────
function AkteSearchField({ valueId, valueLabel, onSelect }: {
  valueId: string | null;
  valueLabel: string | null;
  onSelect: (id: string | null, label: string | null) => void;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    let cancel = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      const esc = term.replace(/([\\%_,()])/g, '\\$1');
      const like = `%${esc}%`;
      const { data } = await supabase.from('akten')
        .select('id, akten_nummer, kennzeichen, titel, kunde_name')
        .or(`akten_nummer.ilike.${like},kennzeichen.ilike.${like},titel.ilike.${like},kunde_name.ilike.${like}`)
        .limit(6);
      if (cancel) return;
      setResults((data || []).map((a: { id: string; akten_nummer?: string; kennzeichen?: string; kunde_name?: string }) => ({
        id: a.id,
        label: [a.akten_nummer, a.kennzeichen, a.kunde_name].filter(Boolean).join(' · ') || 'Akte',
      })));
      setLoading(false);
    }, 250);
    return () => { cancel = true; clearTimeout(handle); };
  }, [q]);

  if (valueId) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
        <Link className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="text-sm text-gray-700 flex-1 truncate">{valueLabel || 'Verknüpfte Akte'}</span>
        <button type="button" onClick={() => { onSelect(null, null); setQ(''); }} className="text-gray-400 hover:text-gray-600" aria-label="Verknüpfung entfernen">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <input type="text" value={q} onChange={e => setQ(e.target.value)}
        placeholder="Aktennr., Kennzeichen oder Kunde suchen…"
        className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
      {term_open(q) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-44 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">Suche…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Keine Akte gefunden</div>}
          {results.map(r => (
            <button key={r.id} type="button" onClick={() => { onSelect(r.id, r.label); setQ(''); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 truncate">{r.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
function term_open(q: string) { return q.trim().length >= 2; }

function KundeSearchField({ valueId, valueLabel, onSelect }: {
  valueId: string | null;
  valueLabel: string | null;
  onSelect: (id: string | null, label: string | null) => void;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    let cancel = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      const like = `%${term}%`;
      const { data } = await supabase.from('kunden')
        .select('id, vorname, nachname, firma')
        .or(`vorname.ilike.${like},nachname.ilike.${like},firma.ilike.${like}`)
        .limit(6);
      if (cancel) return;
      setResults((data || []).map((k: { id: string; vorname?: string; nachname?: string; firma?: string }) => ({
        id: k.id,
        label: k.firma || [k.vorname, k.nachname].filter(Boolean).join(' ') || 'Kunde',
      })));
      setLoading(false);
    }, 250);
    return () => { cancel = true; clearTimeout(handle); };
  }, [q]);

  if (valueId) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
        <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="text-sm text-gray-700 flex-1 truncate">{valueLabel || 'Verknüpfter Kunde'}</span>
        <button type="button" onClick={() => { onSelect(null, null); setQ(''); }} className="text-gray-400 hover:text-gray-600" aria-label="Verknüpfung entfernen">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <input type="text" value={q} onChange={e => setQ(e.target.value)}
        placeholder="Kunde (Name oder Firma) suchen…"
        className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
      {term_open(q) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-44 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">Suche…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Kein Kunde gefunden</div>}
          {results.map(r => (
            <button key={r.id} type="button" onClick={() => { onSelect(r.id, r.label); setQ(''); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 truncate">{r.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function NewEventDialog({ open, onClose, onSubmit, initialDate, defaultBetrieb }: {
  open: boolean; onClose: () => void;
  onSubmit: (event: { summary: string; description: string; location: string; start: string; end: string; businessUnit: string; status: string; kategorie: string; akteId: string | null; kundeId: string | null; erinnerung: number }) => void;
  initialDate?: Date;
  defaultBetrieb?: string;
}) {
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [betrieb, setBetrieb] = useState('gutachten');
  const [status, setStatus] = useState('geplant');
  const [kategorie, setKategorie] = useState('');
  const [erinnerung, setErinnerung] = useState(0);
  const [akteId, setAkteId] = useState<string | null>(null);
  const [akteLabel, setAkteLabel] = useState<string | null>(null);
  const [kundeId, setKundeId] = useState<string | null>(null);
  const [kundeLabel, setKundeLabel] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset state every time dialog opens with fresh defaults
  useEffect(() => {
    if (open) {
      const base = initialDate || new Date();
      const startDefault = new Date(base);
      startDefault.setMinutes(Math.round(startDefault.getMinutes() / 15) * 15, 0, 0);
      const endDefault = new Date(startDefault);
      endDefault.setHours(endDefault.getHours() + 1);
      setSummary('');
      setDescription('');
      setLocation('');
      setBetrieb(defaultBetrieb && defaultBetrieb !== 'alle' ? defaultBetrieb : 'gutachten');
      setStatus('geplant');
      setKategorie('');
      setErinnerung(0);
      setAkteId(null);
      setAkteLabel(null);
      setKundeId(null);
      setKundeLabel(null);
      setStart(toISOLocal(startDefault));
      setEnd(toISOLocal(endDefault));
      setSaving(false);
    }
  }, [open, initialDate, defaultBetrieb]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!summary.trim()) return;
    setSaving(true);
    try {
      await onSubmit({ summary, description, location, start, end, businessUnit: betrieb, status, kategorie, akteId, kundeId, erinnerung });
    } catch (err) {
      // Error handled in parent
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md sm:mx-4 overflow-hidden max-h-[85vh] overflow-y-auto mb-0 sm:mb-0" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(196,30,58,0.08)' }}>
              <Plus className="w-3.5 h-3.5" style={{ color: KOOP_RED }} />
            </div>
            <h3 className="text-sm font-bold text-gray-900">Neuer Termin</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Titel *</label>
            <input type="text" value={summary} onChange={e => setSummary(e.target.value)} required
              placeholder="z.B. Besichtigung VW Golf"
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Start</label>
              <DateTimePicker value={start} onChange={v => setStart(v ?? '')} />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Ende</label>
              <DateTimePicker value={end} onChange={v => setEnd(v ?? '')} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Ort</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
              <input type="text" value={location} onChange={e => setLocation(e.target.value)}
                placeholder="Adresse oder Ort"
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Betrieb</label>
              <select value={betrieb} onChange={e => setBetrieb(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all bg-white">
                {BETRIEB_LIST.filter(b => b.hasData).map(b => <option key={b.id} value={b.id}>{BETRIEBE[b.id]?.name || b.id}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all bg-white">
                <option value="geplant">Geplant</option>
                <option value="bestaetigt">Bestätigt</option>
                <option value="erledigt">Erledigt</option>
                <option value="abgesagt">Abgesagt</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Kategorie</label>
            <select value={kategorie} onChange={e => setKategorie(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all bg-white">
              <option value="">Keine Kategorie</option>
              {kategorienFuerBetrieb(betrieb).map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Akte (optional)</label>
            <AkteSearchField valueId={akteId} valueLabel={akteLabel} onSelect={(id, label) => { setAkteId(id); setAkteLabel(label); }} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Kunde (optional)</label>
            <KundeSearchField valueId={kundeId} valueLabel={kundeLabel} onSelect={(id, label) => { setKundeId(id); setKundeLabel(label); }} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Erinnerung</label>
            <select value={erinnerung} onChange={e => setErinnerung(Number(e.target.value))}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all bg-white">
              <option value={0}>Keine Erinnerung</option>
              <option value={10}>10 Min vorher</option>
              <option value={30}>30 Min vorher</option>
              <option value={60}>1 Std vorher</option>
              <option value={1440}>1 Tag vorher</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Beschreibung</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Notizen zum Termin..." rows={2}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all resize-none" />
          </div>
          {/* Buttons — mobile-dialog-actions ensures visibility above mobile bottom nav */}
          <div className="flex gap-2 pt-1 mobile-dialog-actions sm:pb-0">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
              Abbrechen
            </button>
            <button type="submit" disabled={saving || !summary.trim() || !start || !end}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              style={{ backgroundColor: KOOP_RED }}>
              {saving ? 'Speichern...' : 'Termin erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── EVENT DETAIL POPOVER ──────────────────────────────────────────────────
function EventDetailPopover({ event, onClose, onCreateTask, onEdit, onDelete }: {
  event: CalendarEvent;
  onClose: () => void;
  onCreateTask: (event: CalendarEvent) => void;
  onEdit: (event: CalendarEvent) => void;
  onDelete: (event: CalendarEvent) => void;
}) {
  const startDate = new Date(event.start);
  const endDate = event.end ? new Date(event.end) : null;
  const [akteNr, setAkteNr] = useState<string | null>(event.akteNummer || null);
  useEffect(() => {
    const aid = event.akteId;
    if (!aid || event.akteNummer) return;
    let cancel = false;
    (async () => {
      const { data } = await supabase.from('akten').select('akten_nummer').eq('id', aid).maybeSingle();
      if (!cancel && data) setAkteNr((data as { akten_nummer?: string }).akten_nummer || null);
    })();
    return () => { cancel = true; };
  }, [event.akteId, event.akteNummer]);
  const [kundeName, setKundeName] = useState<string | null>(event.kundeName || null);
  useEffect(() => {
    const kid = event.kundeId;
    if (!kid || event.kundeName) return;
    let cancel = false;
    (async () => {
      const { data } = await supabase.from('kunden').select('vorname, nachname, firma').eq('id', kid).maybeSingle();
      if (!cancel && data) {
        const k = data as { vorname?: string; nachname?: string; firma?: string };
        setKundeName(k.firma || [k.vorname, k.nachname].filter(Boolean).join(' ') || null);
      }
    })();
    return () => { cancel = true; };
  }, [event.kundeId, event.kundeName]);
  const STATUS_META: Record<string, { label: string; cls: string }> = {
    geplant: { label: 'Geplant', cls: 'bg-gray-100 text-gray-600' },
    bestaetigt: { label: 'Bestätigt', cls: 'bg-green-50 text-green-700' },
    erledigt: { label: 'Erledigt', cls: 'bg-blue-50 text-blue-700' },
    abgesagt: { label: 'Abgesagt', cls: 'bg-red-50 text-red-700' },
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/20 backdrop-blur-[2px]" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm sm:mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Color bar */}
        <div className="h-1.5 w-full" style={{ backgroundColor: event.color }} />
        
        <div className="p-4 space-y-3">
          {/* Title */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-bold text-gray-900 flex-1">{event.title}</h3>
            <button onClick={onClose} className="w-11 h-11 sm:w-6 sm:h-6 rounded flex items-center justify-center hover:bg-gray-100 flex-shrink-0">
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>

          {/* Time */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            <span>
              {startDate.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}
              {' · '}
              {startDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              {endDate && ` – ${endDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}
            </span>
          </div>

          {/* Location */}
          {event.location && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <MapPin className="w-3.5 h-3.5" />
              <span>{event.location}</span>
            </div>
          )}

          {/* Source badge */}
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
              {event.source === 'google' ? 'Google Calendar' : event.businessUnit ? BETRIEBE[event.businessUnit]?.name : 'Supabase'}
            </span>
            {event.kategorie && (
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                {event.kategorie}
              </span>
            )}
            {event.source === 'supabase' && event.status && STATUS_META[event.status] && (
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${STATUS_META[event.status].cls}`}>
                {STATUS_META[event.status].label}
              </span>
            )}
            {akteNr && (
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 inline-flex items-center gap-1">
                <Link className="w-2.5 h-2.5" /> Akte {akteNr}
              </span>
            )}
            {kundeName && (
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 inline-flex items-center gap-1">
                <Users className="w-2.5 h-2.5" /> {kundeName}
              </span>
            )}
          </div>

          {/* Description */}
          {event.beschreibung && (
            <p className="text-xs text-gray-500 leading-relaxed">{event.beschreibung}</p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-gray-100 mobile-dialog-actions sm:pb-0">
            <button
              onClick={() => { onClose(); onEdit(event); }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
            >
              <Pencil className="w-3.5 h-3.5" />
              Bearbeiten
            </button>
            <button
              onClick={() => onCreateTask(event)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{ backgroundColor: KOOP_RED }}
            >
              <CheckSquare className="w-3.5 h-3.5" />
              Aufgabe
            </button>
            <button
              onClick={() => onDelete(event)}
              className="flex items-center justify-center w-10 h-10 rounded-xl text-xs font-bold border border-red-200 text-red-500 hover:bg-red-50 transition-all flex-shrink-0"
              title="Termin löschen"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EDIT EVENT MODAL ────────────────────────────────────────────────────────
function EditEventModal({ event, onClose, onSave }: {
  event: CalendarEvent;
  onClose: () => void;
  onSave: (updated: { title: string; start: string; end: string; description: string; location: string; kategorie: string; status: string; akteId: string | null; kundeId: string | null; erinnerung: number }) => Promise<void>;
}) {
  const [title, setTitle] = useState(event.title);
  const [start, setStart] = useState(toISOLocal(new Date(event.start)));
  const [end, setEnd] = useState(event.end ? toISOLocal(new Date(event.end)) : toISOLocal(new Date(new Date(event.start).getTime() + 3600000)));
  const [description, setDescription] = useState(event.beschreibung || '');
  const [location, setLocation] = useState(event.location || '');
  const [kategorie, setKategorie] = useState(event.kategorie || '');
  const [status, setStatus] = useState((event as { status?: string }).status || 'geplant');
  const [akteId, setAkteId] = useState<string | null>(event.akteId || null);
  const [akteLabel, setAkteLabel] = useState<string | null>(event.akteNummer ? `Akte ${event.akteNummer}` : null);
  const [kundeId, setKundeId] = useState<string | null>(event.kundeId || null);
  const [kundeLabel, setKundeLabel] = useState<string | null>(event.kundeName || null);
  const [erinnerung, setErinnerung] = useState((event as { erinnerungMinuten?: number }).erinnerungMinuten || 0);
  const [saving, setSaving] = useState(false);

  const KATEGORIEN = kategorienFuerBetrieb(event.businessUnit || 'gutachten');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({ title, start, end, description, location, kategorie, status, akteId, kundeId, erinnerung });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md sm:mx-4 overflow-hidden max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${event.color}15` }}>
              <Calendar className="w-4 h-4" style={{ color: event.color }} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Termin bearbeiten</h3>
              <p className="text-[10px] text-gray-400">
                {event.source === 'google' ? 'Über Google Calendar' : 'Supabase Termin'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-11 h-11 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Titel *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} required
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Start</label>
              <DateTimePicker value={start} onChange={v => setStart(v ?? '')} />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Ende</label>
              <DateTimePicker value={end} onChange={v => setEnd(v ?? '')} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Ort</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
              <input type="text" value={location} onChange={e => setLocation(e.target.value)}
                placeholder="Adresse oder Ort"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
            </div>
          </div>
          {event.source === 'supabase' && (
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Kategorie</label>
              <select value={kategorie} onChange={e => setKategorie(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all bg-white">
                <option value="">Keine Kategorie</option>
                {KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          )}
          {event.source === 'supabase' && (
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all bg-white">
                <option value="geplant">Geplant</option>
                <option value="bestaetigt">Bestätigt</option>
                <option value="erledigt">Erledigt</option>
                <option value="abgesagt">Abgesagt</option>
              </select>
            </div>
          )}
          {event.source === 'supabase' && (
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Akte (optional)</label>
              <AkteSearchField valueId={akteId} valueLabel={akteLabel} onSelect={(id, label) => { setAkteId(id); setAkteLabel(label); }} />
            </div>
          )}
          {event.source === 'supabase' && (
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Kunde (optional)</label>
              <KundeSearchField valueId={kundeId} valueLabel={kundeLabel} onSelect={(id, label) => { setKundeId(id); setKundeLabel(label); }} />
            </div>
          )}
          {event.source === 'supabase' && (
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Erinnerung</label>
              <select value={erinnerung} onChange={e => setErinnerung(Number(e.target.value))}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all bg-white">
                <option value={0}>Keine Erinnerung</option>
                <option value={10}>10 Min vorher</option>
                <option value={30}>30 Min vorher</option>
                <option value={60}>1 Std vorher</option>
                <option value={1440}>1 Tag vorher</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Beschreibung</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Notizen zum Termin..." rows={2}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all resize-none" />
          </div>
          <div className="flex gap-2 pt-1 mobile-dialog-actions sm:pb-0">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
              Abbrechen
            </button>
            <button type="submit" disabled={saving || !title.trim() || !start || !end}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              style={{ backgroundColor: KOOP_RED }}>
              {saving ? 'Speichern...' : 'Änderungen speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── MAIN KALENDER PAGE ─────────────────────────────────────────────────────
export default function KalenderPage() {
  const isMobile = useIsMobile();
  const [view, setView] = useState<CalendarView>('day'); // Start with day, useEffect will correct
  const [currentDate, setCurrentDate] = useState(new Date());
  const [betriebFilter, setBetriebFilter] = useState<string>('alle');
  const [showFilter, setShowFilter] = useState(false);
  const [showGoogleEvents, setShowGoogleEvents] = useState(true);
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEventDate, setNewEventDate] = useState<Date | undefined>();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [taskModalEvent, setTaskModalEvent] = useState<CalendarEvent | null>(null);
  const [editEvent, setEditEvent] = useState<CalendarEvent | null>(null);

  // Mobile: Hardware-Back schließt Event-Detail/Edit statt Seite zu verlassen
  useMobileBackHandler(!!selectedEvent, () => setSelectedEvent(null));
  useMobileBackHandler(!!editEvent, () => setEditEvent(null));
  const initialViewSet = useRef(false);

  // Drag & Drop State
  const [dragEvent, setDragEvent] = useState<CalendarEvent | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);

  // ── Reactive mobile view switching ──
  // On mobile: default to day view; on desktop: default to week view
  // Also switch when resizing (e.g. orientation change)
  useEffect(() => {
    if (!initialViewSet.current) {
      setView(isMobile ? 'day' : 'week');
      initialViewSet.current = true;
    } else if (isMobile && view === 'week') {
      // If user rotates to portrait while in week view, switch to day
      setView('day');
    }
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Data
  const { isAdmin } = useAuth();
  const { data: termine, loading: termineLoading, refetch: refetchTermine } = useTermine(
    betriebFilter !== 'alle' ? betriebFilter as BusinessUnit : undefined
  );
  const gcal = useGoogleCalendar();
  const [googleEvents, setGoogleEvents] = useState<GoogleCalendarEvent[]>([]);
  const [gcalBannerDismissed, setGcalBannerDismissed] = useState(false);

  // Fetch Google Calendar events — ALWAYS try to load (shared token fallback in Edge Function)
  // ROBUST: Don't depend on isConnected — just try to fetch, Edge Function handles token fallback
  const gcalFetched = useRef(false);
  useEffect(() => {
    if (!gcalFetched.current) {
      gcalFetched.current = true;
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      // Try to fetch events directly — Edge Function will use shared token if no own token
      gcal.fetchEvents(start.toISOString(), end.toISOString()).then(events => {
        if (events && events.length > 0) {
          setGoogleEvents(events);
        }
      }).catch(() => { /* silent — no Google events available */ });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Also retry when connection state changes to true
  useEffect(() => {
    if (gcal.isConnected && gcalFetched.current) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      gcal.fetchEvents(start.toISOString(), end.toISOString()).then(events => {
        if (events && events.length > 0) {
          setGoogleEvents(events);
        }
      }).catch(() => {});
    }
  }, [gcal.isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Current time for time line
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Erinnerungen: Browser-Benachrichtigung einmalig erlauben
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Erinnerungen prüfen (solange die App offen ist): Toast + Notification
  const remindedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!termine) return;
    const now = Date.now();
    for (const t of termine) {
      const mins = (t as { erinnerung_minuten?: number }).erinnerung_minuten;
      if (!mins || !t.beginn) continue;
      const start = new Date(t.beginn).getTime();
      const remindAt = start - mins * 60000;
      const key = `${t.id}@${t.beginn}`;
      if (now >= remindAt && now < start && !remindedRef.current.has(key)) {
        remindedRef.current.add(key);
        const when = new Date(t.beginn).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        toast(`🔔 Erinnerung: ${t.titel} um ${when} Uhr`);
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try { new Notification('Termin-Erinnerung', { body: `${t.titel} — ${when} Uhr` }); } catch { /* ignore */ }
        }
      }
    }
  }, [currentTime, termine]);

  // Merge events
  const allEvents = useMemo((): CalendarEvent[] => {
    const supabaseEvents = (termine || [])
      .filter(t => betriebFilter === 'alle' || t.business_unit === betriebFilter)
      .map(t => ({
        id: t.id,
        title: t.titel,
        start: t.beginn,
        end: t.ende,
        source: 'supabase' as const,
        businessUnit: t.business_unit as BetriebId,
        color: BETRIEBE[t.business_unit as BetriebId]?.color?.accent || KOOP_RED,
        bgColor: BETRIEBE[t.business_unit as BetriebId]?.color?.bgLight || 'rgba(196,30,58,0.06)',
        location: t.ort || undefined,
        htmlLink: undefined as string | undefined,
        beschreibung: t.beschreibung || undefined,
        kategorie: (t as any).kategorie || undefined,
        status: (t as any).status || 'geplant',
        akteId: (t as any).akte_id || undefined,
        kundeId: (t as any).kunde_id || undefined,
        erinnerungMinuten: (t as any).erinnerung_minuten || undefined,
      }));

    const gEvents = showGoogleEvents ? dedupeGoogleEvents(googleEvents || [], termine || []).map(e => ({
      id: e.id,
      title: e.summary || '(Kein Titel)',
      start: e.start,
      end: e.end,
      source: 'google' as const,
      businessUnit: undefined as BetriebId | undefined,
      color: GCAL_COLORS[e.color_id] || '#4285F4',
      bgColor: `${GCAL_COLORS[e.color_id] || '#4285F4'}15`,
      location: e.location || undefined,
      htmlLink: e.html_link,
      beschreibung: undefined as string | undefined,
      kategorie: undefined as string | undefined,
      status: undefined as string | undefined,
      akteId: undefined as string | undefined,
      kundeId: undefined as string | undefined,
      erinnerungMinuten: undefined as number | undefined,
    })) : [];

    return [...supabaseEvents, ...gEvents];
  }, [termine, googleEvents, betriebFilter, showGoogleEvents]);

  // Navigation
  const navigate = useCallback((dir: number) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  }, [currentDate, view]);

  const goToday = useCallback(() => setCurrentDate(new Date()), []);

  const getEventsForDate = useCallback((date: Date) => {
    return allEvents.filter(e => {
      const eDate = new Date(e.start);
      return isSameDay(eDate, date);
    });
  }, [allEvents]);

  const headerLabel = useMemo(() => {
    if (view === 'day') {
      if (isMobile) {
        return currentDate.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });
      }
      return currentDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (view === 'week') {
      const week = getWeekDates(currentDate);
      return `${week[0].toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })} – ${week[6].toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  }, [currentDate, view, isMobile]);

  // Micro-Relativ-Hinweis (immer ausgehend von heute) + Kalenderwoche
  const relInfo = useMemo(() => ({
    kw: getISOWeek(currentDate),
    rel: relativeLabel(view, currentDate),
  }), [view, currentDate]);

  // Helper: Refresh Google Calendar events after mutation
  const refreshGoogleEvents = useCallback(async () => {
    try {
      const now = new Date();
      const rangeStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const rangeEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      const events = await gcal.fetchEvents(rangeStart.toISOString(), rangeEnd.toISOString());
      setGoogleEvents(events);
    } catch { /* silent */ }
  }, [gcal]);

  // Create event handler — ROBUST: Google Calendar + Supabase Fallback
  const handleCreateEvent = useCallback(async (event: { summary: string; description: string; location: string; start: string; end: string; businessUnit?: string; status?: string; kategorie?: string; akteId?: string | null; kundeId?: string | null; erinnerung?: number }) => {

    try {
      // Try Google Calendar first — ALWAYS try (shared token fallback in Edge Function)
      if (gcal.isConnected || googleEvents.length > 0) {
        const created = await gcal.createEvent({
          summary: event.summary,
          description: event.description,
          location: event.location,
          start: new Date(event.start).toISOString(),
          end: new Date(event.end).toISOString(),
        });
        if (created) {
          // Mirror in `termine`, damit App-Termin & Google-Event verknüpft sind (Dedup verhindert Doppelanzeige)
          const gid = (created as { id?: string } | null)?.id;
          if (gid) {
            try {
              await supabase.from('termine').insert({
                ...buildTerminLinkPayload(
                  event,
                  gid,
                  event.businessUnit || (betriebFilter !== 'alle' ? betriebFilter : 'gutachten'),
                ),
                status: event.status || 'geplant',
                kategorie: event.kategorie || null,
                akte_id: event.akteId || null,
                kunde_id: event.kundeId || null,
                erinnerung_minuten: event.erinnerung || null,
              });
              refetchTermine();
            } catch (linkErr) {
              console.warn('Termine-Verknüpfung fehlgeschlagen (Google-Event trotzdem erstellt):', linkErr);
            }
          }
          await refreshGoogleEvents();
          toast.success('Termin in Google Calendar erstellt');
          return;
        }
        // If Google failed, fall through to Supabase
        console.warn('Google Calendar create failed, falling back to Supabase');
      }

      // Supabase Fallback — always works
      const { error } = await supabase.from('termine').insert({
        titel: event.summary,
        beschreibung: event.description || null,
        ort: event.location || null,
        beginn: new Date(event.start).toISOString(),
        ende: new Date(event.end).toISOString(),
        business_unit: event.businessUnit || (betriebFilter !== 'alle' ? betriebFilter : 'gutachten'),
        status: event.status || 'geplant',
        kategorie: event.kategorie || null,
        akte_id: event.akteId || null,
        kunde_id: event.kundeId || null,
        erinnerung_minuten: event.erinnerung || null,
        ganztaegig: false,
      });

      if (error) throw error;
      refetchTermine();
      toast.success('Termin erstellt');
    } catch (err: any) {
      console.error('Event creation failed:', err);
      toast.error(`Termin konnte nicht erstellt werden: ${err?.message || 'Unbekannter Fehler'}`);
    }
  }, [gcal, betriebFilter, refetchTermine, refreshGoogleEvents]);

  // Handle event click → show detail popover
  const handleEventClick = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event);
  }, []);

  // Handle event double-click → open edit modal
  const handleEventDoubleClick = useCallback((event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedEvent(null);
    setEditEvent(event);
  }, []);

  // Handle event update from edit modal
  const handleUpdateEvent = useCallback(async (updated: { title: string; start: string; end: string; description: string; location: string; kategorie: string; status: string; akteId: string | null; kundeId: string | null; erinnerung: number }) => {
    if (!editEvent) return;
    try {
      if (editEvent.source === 'google') {
        await gcal.updateEvent(editEvent.id, {
          summary: updated.title,
          description: updated.description,
          location: updated.location,
          start: new Date(updated.start).toISOString(),
          end: new Date(updated.end).toISOString(),
        });
        const now = new Date();
        const rangeStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const rangeEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
        const events = await gcal.fetchEvents(rangeStart.toISOString(), rangeEnd.toISOString());
        setGoogleEvents(events);
      } else {
        await supabase.from('termine').update({
          titel: updated.title,
          beschreibung: updated.description || null,
          ort: updated.location || null,
          beginn: new Date(updated.start).toISOString(),
          ende: new Date(updated.end).toISOString(),
          kategorie: updated.kategorie || null,
          status: updated.status || 'geplant',
          akte_id: updated.akteId || null,
          kunde_id: updated.kundeId || null,
          erinnerung_minuten: updated.erinnerung || null,
        }).eq('id', editEvent.id);
        refetchTermine();
      }
      toast.success('Termin aktualisiert');
      setEditEvent(null);
    } catch (err) {
      toast.error('Termin konnte nicht gespeichert werden');
    }
  }, [editEvent, gcal, refetchTermine]);

  // Handle "Create Task" from event
  const handleCreateTaskFromEvent = useCallback((event: CalendarEvent) => {
    setSelectedEvent(null);
    setTaskModalEvent(event);
  }, []);

  // Handle event delete — ROBUST: Google Calendar + Supabase
  const handleDeleteEvent = useCallback(async (event: CalendarEvent) => {
    const confirmed = window.confirm(`Termin "${event.title}" wirklich löschen?`);
    if (!confirmed) return;

    try {
      if (event.source === 'google') {
        const success = await gcal.deleteEvent(event.id);
        if (!success) throw new Error('Google Calendar Löschung fehlgeschlagen');
        await refreshGoogleEvents();
      } else {
        const { error } = await supabase.from('termine').delete().eq('id', event.id);
        if (error) throw error;
        refetchTermine();
      }
      setSelectedEvent(null);
      toast.success('Termin gelöscht');
    } catch (err: any) {
      toast.error(`Termin konnte nicht gelöscht werden: ${err?.message || 'Unbekannter Fehler'}`);
    }
  }, [gcal, refetchTermine, refreshGoogleEvents]);

  // Drag & Drop: Snap to 15-minute grid
  const snapTo30 = (minutes: number) => Math.round(minutes / 30) * 30;

  // Drag & Drop: Update event time in DB or Google Calendar
  const handleEventDrop = useCallback(async (
    event: CalendarEvent,
    newDateStr: string,
    newHour: number,
    newMinute: number
  ) => {
    const snappedMin = snapTo30(newMinute);
    const oldStart = new Date(event.start);
    const oldEnd = event.end ? new Date(event.end) : new Date(oldStart.getTime() + 60 * 60000);
    const durationMs = oldEnd.getTime() - oldStart.getTime();

    const [year, month, day] = newDateStr.split('-').map(Number);
    const newStart = new Date(year, month - 1, day, newHour, snappedMin, 0, 0);
    const newEnd = new Date(newStart.getTime() + durationMs);


    try {
      if (event.source === 'google') {
        await gcal.updateEvent(event.id, {
          start: newStart.toISOString(),
          end: newEnd.toISOString(),
        });
        const now = new Date();
        const rangeStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const rangeEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
        const events = await gcal.fetchEvents(rangeStart.toISOString(), rangeEnd.toISOString());
        setGoogleEvents(events);
      } else {
        await supabase.from('termine').update({
          beginn: newStart.toISOString(),
          ende: newEnd.toISOString(),
        }).eq('id', event.id);
        refetchTermine();
      }
      toast.success(`Termin verschoben auf ${newStart.toLocaleString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`);
    } catch (err) {
      toast.error('Termin konnte nicht verschoben werden');
    }
  }, [gcal, refetchTermine]);

  // ── Pointer-basiertes Drag&Drop (Echtzeit) für Tag/Woche ──
  type DropTarget = { dateStr: string; startHour: number; durationH: number };
  const dragRef = useRef<{
    event: CalendarEvent; grabOffsetMin: number; startX: number; startY: number; moved: boolean;
    gridEl: HTMLElement; timeColWidth: number; startHour: number; totalHours: number; totalPx: number; colCount: number; dates: string[];
    lastTarget: DropTarget | null;
  } | null>(null);
  const weekGridRef = useRef<HTMLDivElement | null>(null);
  const dayGridRef = useRef<HTMLDivElement | null>(null);
  const [dropPreview, setDropPreview] = useState<DropTarget | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const ptrTarget = (clientX: number, clientY: number): DropTarget | null => {
    const d = dragRef.current;
    if (!d) return null;
    const rect = d.gridEl.getBoundingClientRect();
    const colW = (rect.width - d.timeColWidth) / d.colCount;
    let ci = Math.floor((clientX - rect.left - d.timeColWidth) / colW);
    ci = Math.max(0, Math.min(d.colCount - 1, ci));
    const relY = clientY - rect.top;
    const rawHour = d.startHour + (relY / d.totalPx) * d.totalHours;
    const startHour = snapHalfHour(rawHour, d.startHour, d.startHour + d.totalHours - 0.5);
    const durationH = eventDurationHours(d.event.start, d.event.end);
    return { dateStr: d.dates[ci], startHour, durationH };
  };

  const ptrMove = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.moved && Math.abs(e.clientX - d.startX) < 4 && Math.abs(e.clientY - d.startY) < 4) return;
    if (!d.moved) { d.moved = true; setDraggingId(d.event.id); }
    const t = ptrTarget(e.clientX, e.clientY);
    if (t) { d.lastTarget = t; setDropPreview(t); }
  }, []);

  // Drop nutzt EXAKT die zuletzt angezeigte Markierung -> landet immer im gezeigten Kästchen
  const ptrUp = useCallback(() => {
    window.removeEventListener('pointermove', ptrMove);
    const d = dragRef.current;
    dragRef.current = null;
    setDraggingId(null);
    setDropPreview(null);
    if (d && d.moved && d.lastTarget) {
      // Synthetischen Klick direkt nach dem Drag verschlucken (sonst oeffnet sich der Termin-Dialog)
      const swallow = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); window.removeEventListener('click', swallow, true); };
      window.addEventListener('click', swallow, true);
      setTimeout(() => window.removeEventListener('click', swallow, true), 400);
      const t = d.lastTarget;
      const { hour, minute } = splitHour(t.startHour);
      handleEventDrop(d.event, t.dateStr, hour, minute);
    }
  }, [ptrMove, handleEventDrop]);

  const startPtrDrag = (
    e: React.PointerEvent, event: CalendarEvent, gridEl: HTMLElement | null,
    timeColWidth: number, startHour: number, totalHours: number, totalPx: number, colCount: number, dates: string[],
  ) => {
    if (isMobile || e.button !== 0 || !gridEl) return;
    const cardRect = e.currentTarget.getBoundingClientRect();
    const grabOffsetMin = ((e.clientY - cardRect.top) / totalPx) * totalHours * 60;
    dragRef.current = { event, grabOffsetMin, startX: e.clientX, startY: e.clientY, moved: false, gridEl, timeColWidth, startHour, totalHours, totalPx, colCount, dates, lastTarget: null };
    window.addEventListener('pointermove', ptrMove);
    window.addEventListener('pointerup', ptrUp, { once: true });
  };

  // ── Month View ──
  const renderAgenda = () => {
    const today = new Date();
    const startDay = new Date(currentDate); startDay.setHours(0, 0, 0, 0);
    const horizonEnd = new Date(startDay); horizonEnd.setDate(horizonEnd.getDate() + 30);
    const AGENDA_STATUS: Record<string, { label: string; cls: string }> = {
      geplant: { label: 'Geplant', cls: 'bg-gray-100 text-gray-600' },
      bestaetigt: { label: 'Bestätigt', cls: 'bg-green-50 text-green-700' },
      erledigt: { label: 'Erledigt', cls: 'bg-blue-50 text-blue-700' },
      abgesagt: { label: 'Abgesagt', cls: 'bg-red-50 text-red-700' },
    };
    const upcoming = allEvents
      .filter(e => { const d = new Date(e.start); return d >= startDay && d < horizonEnd; })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    const groups: { key: string; date: Date; events: CalendarEvent[] }[] = [];
    for (const ev of upcoming) {
      const d = new Date(ev.start);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      let g = groups.find(x => x.key === key);
      if (!g) { g = { key, date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), events: [] }; groups.push(g); }
      g.events.push(ev);
    }
    if (groups.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Calendar className="w-8 h-8 text-gray-200 mb-2" />
          <p className="text-sm text-gray-400">Keine Termine in den nächsten 30 Tagen</p>
        </div>
      );
    }
    return (
      <div className="max-w-2xl mx-auto divide-y divide-gray-100">
        {groups.map(g => {
          const isToday = isSameDay(g.date, today);
          return (
            <div key={g.key} className="py-3">
              <div className="flex items-center gap-2 px-2 mb-2">
                <span className={`text-xs font-bold ${isToday ? 'text-[#1a73e8]' : 'text-gray-700'}`}>
                  {g.date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
                </span>
                {isToday && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-[#1a73e8]">Heute</span>}
                <span className="text-[10px] text-gray-300 ml-auto">{g.events.length} Termin{g.events.length > 1 ? 'e' : ''}</span>
              </div>
              <div className="space-y-1.5">
                {g.events.map((ev, i) => {
                  const sd = new Date(ev.start);
                  const ed = ev.end ? new Date(ev.end) : null;
                  return (
                    <button key={ev.id + '-' + i} onClick={() => handleEventClick(ev)}
                      className="w-full flex items-stretch gap-2.5 text-left rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all p-2">
                      <div className="w-1 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color }} />
                      <div className="flex flex-col items-center justify-center w-12 flex-shrink-0">
                        <span className="text-xs font-bold text-gray-700">{sd.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                        {ed && <span className="text-[10px] text-gray-400">{ed.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>}
                      </div>
                      <div className="flex-1 min-w-0 py-0.5">
                        <span className="block text-sm font-medium text-gray-900 truncate">{ev.title}</span>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {ev.source === 'google' ? (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">Google</span>
                          ) : ev.businessUnit ? (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: ev.bgColor, color: ev.color }}>{BETRIEBE[ev.businessUnit]?.name || ev.businessUnit}</span>
                          ) : null}
                          {ev.source === 'supabase' && ev.status && AGENDA_STATUS[ev.status] && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${AGENDA_STATUS[ev.status].cls}`}>{AGENDA_STATUS[ev.status].label}</span>
                          )}
                          {ev.location && <span className="text-[10px] text-gray-400 inline-flex items-center gap-0.5 truncate max-w-[140px]"><MapPin className="w-2.5 h-2.5 flex-shrink-0" />{ev.location}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = new Date(year, month, 1).getDay();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    const today = new Date();

    const cells: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = startOffset - 1; i >= 0; i--) {
      cells.push({ date: new Date(year, month, -i), isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
      cells.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return (
      <div>
        <div className="grid grid-cols-7 mb-1 sticky top-0 z-30 bg-white pb-1">
          {(isMobile ? ['M', 'D', 'M', 'D', 'F', 'S', 'S'] : ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']).map((d, i) => (
            <div key={i} className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-gray-100 rounded-xl overflow-hidden">
          {cells.map((cell, i) => {
            const dayEvents = getEventsForDate(cell.date);
            const isToday = isSameDay(cell.date, today);
            const maxEventsShown = isMobile ? 2 : 3;
            return (
              <button key={i}
                onClick={() => { setCurrentDate(cell.date); setView('day'); }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverDate(localIsoDate(cell.date)); }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!dragEvent) return;
                  const sd = new Date(dragEvent.start);
                  handleEventDrop(dragEvent, localIsoDate(cell.date), sd.getHours(), sd.getMinutes());
                  setDragEvent(null); setDragOverDate(null);
                }}
                className={`min-h-[48px] sm:min-h-[80px] p-1 sm:p-1.5 text-left transition-all hover:bg-blue-50/50 relative ${
                  cell.isCurrentMonth ? 'bg-white' : 'bg-gray-50/50'
                } ${dragEvent && dragOverDate === localIsoDate(cell.date) ? 'ring-2 ring-inset ring-[#1a73e8] bg-blue-50' : isToday ? 'ring-2 ring-inset ring-blue-200 bg-blue-50/30' : ''}`}
              >
                <span className={`text-[10px] sm:text-xs font-bold block mb-0.5 ${
                  isToday ? 'text-white bg-[#1a73e8] w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[9px] sm:text-xs' :
                  cell.isCurrentMonth ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  {cell.date.getDate()}
                </span>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, maxEventsShown).map((evt, j) => (
                    <div key={j} draggable={!isMobile}
                      onDragStart={(e) => { e.stopPropagation(); setDragEvent(evt); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragEnd={() => { setDragEvent(null); setDragOverDate(null); }}
                      className={`text-[7px] sm:text-[9px] font-medium rounded px-0.5 sm:px-1.5 py-0.5 truncate ${isMobile ? '' : 'cursor-grab active:cursor-grabbing'}`}
                      style={{ backgroundColor: '#f1f3f4', color: '#3c4043', borderLeft: `2px solid ${evt.color}` }}
                      onClick={(e) => { e.stopPropagation(); handleEventClick(evt); }}>
                      {isMobile ? '' : `${formatTime(evt.start)} `}{evt.title?.substring(0, isMobile ? 4 : 12)}
                    </div>
                  ))}
                  {dayEvents.length > maxEventsShown && (
                    <div className="text-[7px] sm:text-[9px] font-bold text-gray-400 px-0.5 sm:px-1">+{dayEvents.length - maxEventsShown}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Week View ──
  const renderWeek = () => {
    const weekDates = getWeekDates(currentDate);
    const today = new Date();
    // Mobile: kompaktere Slots
    const SLOT_H = isMobile ? 28 : 40;
    const HOUR_H = SLOT_H * 2;
    const START_HOUR = 6;
    const TOTAL_HOURS = 15;
    const TOTAL_PX = TOTAL_HOURS * HOUR_H;
    const isCurrentWeek = weekDates.some(d => isSameDay(d, today));
    const now = currentTime;
    const nowH = now.getHours() + now.getMinutes() / 60;
    const timeLinePos = isCurrentWeek && nowH >= START_HOUR && nowH <= START_HOUR + TOTAL_HOURS
      ? ((nowH - START_HOUR) / TOTAL_HOURS) * TOTAL_PX
      : null;

    const slots = Array.from({ length: TOTAL_HOURS * 2 }, (_, i) => {
      const h = START_HOUR + Math.floor(i / 2);
      const m = i % 2 === 0 ? '00' : '30';
      return { label: m === '00' ? `${String(h).padStart(2, '0')}:00` : '', isHour: m === '00', top: i * SLOT_H };
    });

    const getEventStyle = (event: CalendarEvent) => {
      const startDate = new Date(event.start);
      const endDate = event.end ? new Date(event.end) : new Date(startDate.getTime() + 60 * 60000);
      const startH = startDate.getHours() + startDate.getMinutes() / 60;
      const endH = endDate.getHours() + endDate.getMinutes() / 60;
      const top = Math.max(0, (startH - START_HOUR) / TOTAL_HOURS) * TOTAL_PX;
      const height = Math.max(20, ((endH - startH) / TOTAL_HOURS) * TOTAL_PX - 2);
      return { top, height };
    };

    // On mobile, show 3 days centered on current day
    const displayDates = isMobile ? (() => {
      const currentDayIndex = weekDates.findIndex(d => isSameDay(d, currentDate));
      const startIdx = Math.max(0, Math.min(currentDayIndex - 1, 4));
      return weekDates.slice(startIdx, startIdx + 3);
    })() : weekDates;
    const colCount = displayDates.length;
    const timeColWidth = isMobile ? 36 : 48;

    return (
      <div className="pb-2">
        <div className="w-full">
          <div className={`grid border-b border-gray-200 bg-white sticky top-0 z-30`}
            style={{ gridTemplateColumns: `${timeColWidth}px repeat(${colCount}, 1fr)` }}>
            <div className="py-1.5 sm:py-3" />
            {displayDates.map((d, i) => {
              const isToday = isSameDay(d, today);
              return (
                <button key={i} onClick={() => { setCurrentDate(d); setView('day'); }}
                  className={`text-center py-1.5 sm:py-3 transition-colors hover:bg-gray-50 border-l border-gray-100 ${isToday ? 'bg-red-50/40' : ''}`}>
                  <div className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                    {d.toLocaleDateString('de-DE', { weekday: 'short' })}
                  </div>
                  <div className={`text-xs sm:text-base font-bold mt-0.5 mx-auto w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-[#1a73e8] text-white' : 'text-gray-700'
                  }`}>
                    {d.getDate()}
                  </div>
                </button>
              );
            })}
          </div>
          <div ref={weekGridRef} className="relative" style={{ height: TOTAL_PX }}>
            {slots.map((slot, i) => (
              <div key={i} className="absolute left-0 right-0 pointer-events-none" style={{ top: slot.top }}>
                <div className="absolute right-0 border-t"
                  style={{ left: timeColWidth, borderColor: slot.isHour ? '#e5e7eb' : '#f1f3f4', borderStyle: slot.isHour ? 'solid' : 'dashed' }} />
                {slot.label && (
                  <div className="absolute text-[9px] sm:text-[11px] font-medium text-gray-500 text-right tabular-nums"
                    style={{ left: 0, width: timeColWidth - 6, paddingRight: 4, top: -7 }}>
                    {slot.label}
                  </div>
                )}
              </div>
            ))}
            <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `${timeColWidth}px repeat(${colCount}, 1fr)` }}>
              <div />
              {displayDates.map((d, di) => {
                const isToday = isSameDay(d, today);
                const dayEvents = getEventsForDate(d);
                const positioned = computeEventPositions(dayEvents, START_HOUR, TOTAL_HOURS);
                return (
                  <div
                  key={di}
                  className={`relative border-l border-gray-100 ${isToday ? 'bg-blue-50/20' : ''} ${dropPreview && dropPreview.dateStr === localIsoDate(d) ? 'bg-blue-50/40' : ''}`}
                  onClick={(e) => {
                    if (e.target !== e.currentTarget) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const relY = e.clientY - rect.top;
                    const rawHour = START_HOUR + (relY / TOTAL_PX) * TOTAL_HOURS;
                    const hour = Math.floor(Math.max(START_HOUR, Math.min(START_HOUR + TOTAL_HOURS - 1, rawHour)));
                    const minute = snapTo30(Math.floor((rawHour - Math.floor(rawHour)) * 60));
                    const dt = new Date(d); dt.setHours(hour, minute, 0, 0);
                    setNewEventDate(dt); setNewEventOpen(true);
                  }}
                >
                    {dropPreview && dropPreview.dateStr === localIsoDate(d) && (
                      <div className="absolute left-0.5 right-0.5 rounded-md pointer-events-none z-40" style={{
                        top: ((dropPreview.startHour - START_HOUR) / TOTAL_HOURS) * TOTAL_PX,
                        height: (dropPreview.durationH / TOTAL_HOURS) * TOTAL_PX,
                        backgroundColor: 'rgba(26,115,232,0.18)',
                        border: '1.5px solid #1a73e8',
                      }} />
                    )}
                    {Array.from({ length: TOTAL_HOURS * 2 }).map((_, si) => {
                      const ch = START_HOUR + Math.floor(si / 2);
                      const cm = si % 2 === 0 ? 0 : 30;
                      return (
                        <div key={`cell-${si}`}
                          className="absolute left-0 right-0 hover:bg-[rgba(26,115,232,0.06)] transition-colors"
                          style={{ top: (si / (TOTAL_HOURS * 2)) * TOTAL_PX, height: TOTAL_PX / (TOTAL_HOURS * 2) }}
                          onClick={() => { const dt = new Date(d); dt.setHours(ch, cm, 0, 0); setNewEventDate(dt); setNewEventOpen(true); }}
                        />
                      );
                    })}
                    {positioned.map(({ event: evt, column, totalColumns }, ti) => {
                      const { top, height } = getEventStyle(evt);
                      const startDate = new Date(evt.start);
                      const endDate = evt.end ? new Date(evt.end) : null;
                      const GAP = 1;
                      const colWidth = totalColumns > 1 ? `calc(${100 / totalColumns}% - ${GAP}px)` : 'calc(100% - 4px)';
                      const colLeft = totalColumns > 1 ? `calc(${(column / totalColumns) * 100}% + ${column === 0 ? 2 : GAP / 2}px)` : '2px';
                      const isDragging = draggingId === evt.id;
                      return (
                        <div key={ti}
                          onPointerDown={(e) => startPtrDrag(e, evt, weekGridRef.current, timeColWidth, START_HOUR, TOTAL_HOURS, TOTAL_PX, colCount, displayDates.map(localIsoDate))}
                          onClick={() => handleEventClick(evt)}
                          onDoubleClick={(e) => handleEventDoubleClick(evt, e)}
                          className={`absolute rounded-md px-1 py-0.5 overflow-hidden ${isMobile ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'} hover:brightness-95 transition-all group`}
                          style={{
                            top, height,
                            left: colLeft,
                            width: colWidth,
                            backgroundColor: '#f8f9fa',
                            borderLeft: `2px solid ${evt.color}`,
                            boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.18)' : '0 1px 3px rgba(0,0,0,0.08)',
                            zIndex: isDragging ? 50 : 5 + column,
                            opacity: isDragging ? 0.6 : 1,
                            transform: isDragging ? 'scale(0.97)' : 'none',
                          }}>
                          <div className="text-[8px] sm:text-[9px] font-bold truncate" style={{ color: '#5f6368' }}>
                            {startDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {height > 24 && (
                            <div className="text-[8px] sm:text-[9px] font-semibold truncate" style={{ color: '#3c4043' }}>
                              {evt.title}
                            </div>
                          )}
                          {!isMobile && height > 40 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCreateTaskFromEvent(evt); }}
                              className="absolute bottom-1 right-1 w-5 h-5 rounded flex items-center justify-center bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                              title="Aufgabe erstellen"
                            >
                              <CheckSquare className="w-3 h-3 text-gray-500" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {timeLinePos !== null && (
              <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: timeLinePos }}>
                <div className="flex items-center">
                  <div style={{ width: timeColWidth }} className="flex justify-end pr-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
                  </div>
                  <div className="flex-1 h-px bg-[#C41E3A]" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Day View ──
  const renderDay = () => {
    const dayEvents = getEventsForDate(currentDate);
    const today = new Date();
    const isToday = isSameDay(currentDate, today);
    // Mobile: kompaktere Slots für weniger Scrollen
    const SLOT_H = isMobile ? 28 : 40;
    const HOUR_H = SLOT_H * 2;
    const START_HOUR = 6;
    const TOTAL_HOURS = 15;
    const TOTAL_PX = TOTAL_HOURS * HOUR_H;
    const now = currentTime;
    const nowH = now.getHours() + now.getMinutes() / 60;
    const timeLinePosDay = isToday && nowH >= START_HOUR && nowH <= START_HOUR + TOTAL_HOURS
      ? ((nowH - START_HOUR) / TOTAL_HOURS) * TOTAL_PX
      : null;

    const timeColWidth = isMobile ? 36 : 48;

    const slots = Array.from({ length: TOTAL_HOURS * 2 }, (_, i) => {
      const h = START_HOUR + Math.floor(i / 2);
      const m = i % 2 === 0 ? '00' : '30';
      return { label: m === '00' ? `${String(h).padStart(2, '0')}:00` : '', isHour: m === '00', top: i * SLOT_H };
    });

    const getEventStyle = (event: CalendarEvent) => {
      const startDate = new Date(event.start);
      const endDate = event.end ? new Date(event.end) : new Date(startDate.getTime() + 60 * 60000);
      const startH = startDate.getHours() + startDate.getMinutes() / 60;
      const endH = endDate.getHours() + endDate.getMinutes() / 60;
      const top = Math.max(0, (startH - START_HOUR) / TOTAL_HOURS) * TOTAL_PX;
      const height = Math.max(24, ((endH - startH) / TOTAL_HOURS) * TOTAL_PX - 2);
      return { top, height };
    };

    return (
      <div ref={dayGridRef} className="relative" style={{ height: TOTAL_PX }}>
        {slots.map((slot, i) => (
          <div key={i} className="absolute left-0 right-0 pointer-events-none" style={{ top: slot.top }}>
            <div className="absolute right-0 border-t"
              style={{ left: timeColWidth, borderColor: slot.isHour ? '#e5e7eb' : '#f1f3f4', borderStyle: slot.isHour ? 'solid' : 'dashed' }} />
            {slot.label && (
              <div className="absolute text-[9px] sm:text-[11px] font-medium text-gray-500 text-right tabular-nums"
                style={{ left: 0, width: timeColWidth - 6, paddingRight: 4, top: -7 }}>
                {slot.label}
              </div>
            )}
          </div>
        ))}
        <div
          className="absolute inset-0"
          style={{ left: timeColWidth }}
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const relY = e.clientY - rect.top;
            const rawHour = START_HOUR + (relY / TOTAL_PX) * TOTAL_HOURS;
            const hour = Math.floor(Math.max(START_HOUR, Math.min(START_HOUR + TOTAL_HOURS - 1, rawHour)));
            const minute = snapTo30(Math.floor((rawHour - Math.floor(rawHour)) * 60));
            const dt = new Date(currentDate); dt.setHours(hour, minute, 0, 0);
            setNewEventDate(dt); setNewEventOpen(true);
          }}
        >
          {dropPreview && dropPreview.dateStr === localIsoDate(currentDate) && (
            <div className="absolute left-1 right-2 rounded-md pointer-events-none z-40" style={{
              top: ((dropPreview.startHour - START_HOUR) / TOTAL_HOURS) * TOTAL_PX,
              height: (dropPreview.durationH / TOTAL_HOURS) * TOTAL_PX,
              backgroundColor: 'rgba(26,115,232,0.18)',
              border: '1.5px solid #1a73e8',
            }} />
          )}
          {Array.from({ length: TOTAL_HOURS * 2 }).map((_, si) => {
            const ch = START_HOUR + Math.floor(si / 2);
            const cm = si % 2 === 0 ? 0 : 30;
            return (
              <div key={`cell-${si}`}
                className="absolute left-0 right-0 hover:bg-[rgba(26,115,232,0.06)] transition-colors"
                style={{ top: (si / (TOTAL_HOURS * 2)) * TOTAL_PX, height: TOTAL_PX / (TOTAL_HOURS * 2) }}
                onClick={() => { const dt = new Date(currentDate); dt.setHours(ch, cm, 0, 0); setNewEventDate(dt); setNewEventOpen(true); }}
              />
            );
          })}
          {computeEventPositions(dayEvents, START_HOUR, TOTAL_HOURS).map(({ event: evt, column, totalColumns }, i) => {
            const { top, height } = getEventStyle(evt);
            const startDate = new Date(evt.start);
            const endDate = evt.end ? new Date(evt.end) : null;
            const durationMin = endDate ? Math.round((endDate.getTime() - startDate.getTime()) / 60000) : 60;
            const GAP = 4;
            const rightPad = isMobile ? 4 : 8;
            const colWidthPct = 100 / totalColumns;
            const colLeftPct = (column / totalColumns) * 100;
            return (
              <div key={i}
                onPointerDown={(e) => startPtrDrag(e, evt, dayGridRef.current, timeColWidth, START_HOUR, TOTAL_HOURS, TOTAL_PX, 1, [localIsoDate(currentDate)])}
                onClick={() => handleEventClick(evt)}
                onDoubleClick={(e) => handleEventDoubleClick(evt, e)}
                className={`absolute rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 overflow-hidden ${isMobile ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'} hover:brightness-95 transition-all group`}
                style={{
                  top, height,
                  left: `calc(${colLeftPct}% + ${column > 0 ? GAP / 2 : 0}px)`,
                  width: `calc(${colWidthPct}% - ${column > 0 ? GAP / 2 : 0}px - ${column < totalColumns - 1 ? GAP / 2 : rightPad}px)`,
                  backgroundColor: '#f8f9fa',
                  borderLeft: `3px solid ${evt.color}`,
                  boxShadow: draggingId === evt.id ? '0 8px 24px rgba(0,0,0,0.18)' : '0 1px 4px rgba(0,0,0,0.08)',
                  zIndex: draggingId === evt.id ? 50 : 5 + column,
                  opacity: draggingId === evt.id ? 0.6 : 1,
                  transform: draggingId === evt.id ? 'scale(0.97)' : 'none',
                }}>
                <p className="text-[11px] sm:text-xs font-bold truncate" style={{ color: '#3c4043' }}>{evt.title}</p>
                <p className="text-[9px] sm:text-[10px] text-gray-500">
                  {formatTime(evt.start)}{evt.end ? ` – ${formatTime(evt.end)}` : ''}
                  {!isMobile && durationMin > 0 && ` · ${durationMin} Min`}
                </p>
                {height > 48 && evt.location && (
                  <p className="text-[9px] sm:text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {evt.location}
                  </p>
                )}
                {/* Quick task button */}
                {!isMobile && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCreateTaskFromEvent(evt); }}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md flex items-center justify-center bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-white"
                    title="Aufgabe erstellen"
                  >
                    <CheckSquare className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {timeLinePosDay !== null && (
          <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: timeLinePosDay }}>
            <div className="flex items-center">
              <div style={{ width: timeColWidth }} className="flex justify-end pr-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
              </div>
              <div className="flex-1 h-px bg-[#C41E3A]" />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-2 sm:p-4 lg:p-6 space-y-2 sm:space-y-4 h-full flex flex-col">
      {/* Header — hidden on mobile to save space */}
      <div className="hidden sm:flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f1f3f4' }}>
            <Calendar className="w-5 h-5" style={{ color: '#5f6368' }} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Kalender</h1>
            <p className="text-xs text-gray-400">Alle Termine aller Betriebe</p>
          </div>
        </div>
      </div>

      {/* Google Calendar Connection Banner — nur für Admin wenn nicht verbunden */}
      {!gcal.isConnected && !gcal.isLoading && googleEvents.length === 0 && !gcalBannerDismissed && isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-blue-800">Google Calendar nicht verbunden</p>
              <p className="text-xs text-blue-600 truncate">Verbinde Google Calendar um Termine für alle Mitarbeiter sichtbar zu machen</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => gcal.connect()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: '#1a73e8' }}
            >
              <Link className="w-3.5 h-3.5" />
              Verbinden
            </button>
            <button onClick={() => setGcalBannerDismissed(true)} className="p-1 rounded hover:bg-blue-100 transition-colors">
              <X className="w-4 h-4 text-blue-400" />
            </button>
          </div>
        </div>
      )}

      {/* Info-Banner für Mitarbeiter: NUR zeigen wenn Events NICHT geladen werden konnten UND kein Loading */}
      {!gcal.isConnected && !gcal.isLoading && googleEvents.length === 0 && !gcalBannerDismissed && !isAdmin && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-500">Google Calendar-Termine werden geladen...</p>
          </div>
          <button onClick={() => setGcalBannerDismissed(true)} className="p-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0">
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
      )}

      {/* Controls Bar — Mobile: kompakt, Desktop: wie bisher */}
      <div className="bg-white rounded-xl border border-gray-100 px-2 sm:px-4 py-2 sm:py-2.5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {/* Row 1: Navigation + Date Label */}
        <div className="flex items-center justify-between gap-1 sm:gap-2">
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => navigate(-1)} className="w-8 h-8 sm:w-8 sm:h-8 rounded-lg border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors flex-shrink-0">
              <ChevronLeft className="w-4 h-4 text-gray-500" />
            </button>
            <button onClick={goToday}
              className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[9px] sm:text-[10px] font-bold uppercase tracking-wider sm:tracking-widest border border-gray-100 hover:bg-gray-50 text-gray-600 transition-colors flex-shrink-0">
              Heute
            </button>
            <button onClick={() => navigate(1)} className="w-8 h-8 sm:w-8 sm:h-8 rounded-lg border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors flex-shrink-0">
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          <div className="flex-1 min-w-0 text-right ml-1">
            <h4 className="text-[11px] sm:text-sm font-bold text-gray-900 truncate">{headerLabel}</h4>
            <div className="text-[10px] text-gray-400 truncate tabular-nums">KW {relInfo.kw} · {relInfo.rel}</div>
          </div>
        </div>

        {/* Row 2: Actions + View Switcher */}
        <div className="flex items-center justify-between gap-1 sm:gap-2 mt-1.5 sm:mt-2">
          {/* Left: Action buttons */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {googleEvents.length > 0 && (
              <button onClick={() => setShowGoogleEvents(!showGoogleEvents)}
                className={`flex items-center gap-1 px-2 py-1 sm:py-1.5 rounded-lg text-[9px] sm:text-[10px] font-bold transition-all border ${
                  showGoogleEvents ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-gray-50 border-gray-100 text-gray-400'
                }`}>
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5" /></svg>
                <span className="hidden sm:inline">Google</span>
              </button>
            )}
            <button onClick={() => { setNewEventDate(currentDate); setNewEventOpen(true); }}
              className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[9px] sm:text-[10px] font-bold text-white transition-all hover:scale-105 active:scale-95 flex-shrink-0"
              style={{ backgroundColor: KOOP_RED }}>
              <Plus className="w-3 h-3" />
              <span className="hidden sm:inline">Termin</span>
            </button>
            <div className="relative">
              <button onClick={() => setShowFilter(!showFilter)}
                className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[9px] sm:text-[10px] font-bold uppercase tracking-wider sm:tracking-widest border border-gray-100 hover:bg-gray-50 text-gray-500 transition-colors">
                <Filter className="w-3 h-3" />
                <span className="hidden sm:inline">{betriebFilter === 'alle' ? 'Alle' : BETRIEBE[betriebFilter as BetriebId]?.name || betriebFilter}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {showFilter && (
                <div className="absolute left-0 sm:right-0 sm:left-auto top-full mt-1 bg-white rounded-xl border border-gray-100 shadow-lg z-50 py-1 min-w-[160px]">
                  <button onClick={() => { setBetriebFilter('alle'); setShowFilter(false); }}
                    className={`w-full text-left px-3 py-2.5 text-xs hover:bg-gray-50 ${betriebFilter === 'alle' ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
                    Alle Betriebe
                  </button>
                  {BETRIEB_LIST.filter(b => b.hasData).map(b => (
                    <button key={b.id} onClick={() => { setBetriebFilter(b.id); setShowFilter(false); }}
                      className={`w-full text-left px-3 py-2.5 text-xs hover:bg-gray-50 ${betriebFilter === b.id ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
                      {b.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: View Switcher — always fully visible */}
          <div className="flex bg-gray-100/80 rounded-lg p-0.5 flex-shrink-0">
            {([
              { id: 'day' as const, label: isMobile ? 'T' : 'Tag' },
              { id: 'week' as const, label: isMobile ? 'W' : 'Woche' },
              { id: 'month' as const, label: isMobile ? 'M' : 'Monat' },
              { id: 'agenda' as const, label: isMobile ? 'A' : 'Agenda' },
            ]).map(v => (
              <button key={v.id} onClick={() => setView(v.id)}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider sm:tracking-widest transition-all ${
                  view === v.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'
                }`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar Content + Sidebar */}
      <div className="flex-1 flex gap-3 min-h-0">
        <KalenderSidebar
          currentDate={currentDate}
          onPickDate={(d) => { setCurrentDate(d); setView('day'); }}
          betriebFilter={betriebFilter}
          onSetBetrieb={setBetriebFilter}
          onNewEvent={() => { setNewEventDate(currentDate); setNewEventOpen(true); }}
        />
        <div className="flex-1 bg-white rounded-xl border border-gray-100 overflow-hidden min-h-0" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {termineLoading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-5 h-5 animate-spin text-gray-300" />
          </div>
        ) : (
          <div className="p-2 sm:p-4 h-full overflow-auto">
            {view === 'month' && renderMonth()}
            {view === 'week' && renderWeek()}
            {view === 'day' && renderDay()}
            {view === 'agenda' && renderAgenda()}
          </div>
        )}
        </div>
      </div>

      {/* Dialogs */}
      <NewEventDialog
        open={newEventOpen}
        onClose={() => setNewEventOpen(false)}
        onSubmit={handleCreateEvent}
        initialDate={newEventDate}
        defaultBetrieb={betriebFilter}
      />

      {/* Event Detail Popover */}
      {selectedEvent && (
        <EventDetailPopover
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onCreateTask={handleCreateTaskFromEvent}
          onEdit={(evt) => { setSelectedEvent(null); setEditEvent(evt); }}
          onDelete={handleDeleteEvent}
        />
      )}

      {/* Edit Event Modal (Doppelklick) */}
      {editEvent && (
        <EditEventModal
          event={editEvent}
          onClose={() => setEditEvent(null)}
          onSave={handleUpdateEvent}
        />
      )}

      {/* Create Task from Event Modal */}
      {taskModalEvent && (
        <CreateTaskFromEventModal
          open={true}
          onClose={() => setTaskModalEvent(null)}
          event={{
            id: taskModalEvent.id,
            title: taskModalEvent.title,
            start: taskModalEvent.start,
            end: taskModalEvent.end,
            source: taskModalEvent.source,
            businessUnit: taskModalEvent.businessUnit,
            location: taskModalEvent.location,
            description: taskModalEvent.beschreibung,
            kategorie: taskModalEvent.kategorie,
          }}
          onSuccess={() => {
            refetchTermine();
            toast.success('Aufgabe wurde mit dem Termin verknüpft');
          }}
        />
      )}
    </div>
  );
}
